// Gantt Chart — data access.
//
// The chart reads its own endpoint, POST /fetchGanttPortfolio, which returns the
// hierarchy it draws — project -> campaigns -> stages -> batches — carrying only
// the fields it renders: a name, two dates, a status and yield. The status is
// carried at project, campaign and batch level, because FSD F has each of those
// three rows show its OWN system status rather than a rollup of its children.
//
// It used to read the dashboard's /fetchProjectDashboard with pageSize: 500.
// That was wrong twice over. The dashboard is a PAGINATED table feed, so a
// sponsor with more than 500 projects had the chart silently truncated; and its
// rows carry the whole dashboard table shape — descriptions, PO numbers,
// quantities, granularity, every campaign and stage attribute — none of which
// the chart draws, all of it on the wire.
//
// The CMO / Site selectors still reuse the dashboard filter bar's own option
// endpoints, since those lists are identical on both screens.
//
// Nothing here is stubbed; a failure resolves to an empty result of the same
// shape so the screen shows its own empty state instead of throwing.

import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import { getApiBaseUrl } from '../../CommonMethods/CommonMethods';
import { fetchCMOMembersAPI, fetchMfgPlantsAPI } from '../Dashboard/filtersAPI';

/**
 * The whole portfolio, optionally scoped to a CMO and/or site.
 *
 * Response: { status, data: { projects: [{ collabrixProjectID, projectDisplayId,
 *   projectStatus,
 *   campaigns: [{ campaignID, campaignStatus,
 *     campaignStartDate, campaignEndDate,
 *     productQuantityRequired, uom, stages: [{ cmoStageID, stageNumber,
 *       batches: [{ vendorBatchNumber, batchStartDate, batchEndDate,
 *                   statusDescription, batchSize, producedQuantity, batchUOM }]
 *     }] }] }] } }
 *
 * @returns {Promise<{status: boolean, projects: Array}>}
 */
export const fetchGanttPortfolioAPI = async ({ cmoOrgId, cmoSite } = {}) => {
  try {
    const apiBaseUrl = getApiBaseUrl(appUrl);
    if (!apiBaseUrl) return { status: false, projects: [] };

    const response = await axios.post(`${apiBaseUrl}/fetchGanttPortfolio`, {
      cdmoOrgID: cmoOrgId || 'all',
      cmoSite: cmoSite || 'all',
    });

    if (response?.data?.status === true) {
      return { status: true, projects: response.data.data?.projects || [] };
    }
    return { status: false, projects: [] };
  } catch (error) {
    console.error('[GanttChart] Failed to load portfolio:', error);
    return { status: false, projects: [] };
  }
};

/**
 * CMO options for the facility selector (FSD A). Shape: [{ id, label }].
 *
 * The FSD's single hard-coded CMO ('Sai Life Sciences') is one customer's
 * value; the real list is the ecosystem's CMO members, so a sponsor with
 * several CDMOs sees all of them and the default is simply the first. Field
 * names and de-duplication match the dashboard's own filter bar
 * (FilterSection's cmoOptions): a member is { organization_id, organization_name }.
 */
export const fetchGanttCmoOptionsAPI = async () => {
  try {
    const res = await fetchCMOMembersAPI();
    const list = (res && res.data) || [];
    const seen = new Set();
    const options = [];
    list.forEach((m) => {
      if (!m || m.organization_id == null) return;
      const id = String(m.organization_id);
      if (seen.has(id)) return;
      seen.add(id);
      options.push({ id, label: m.organization_name || id });
    });
    return { status: true, data: options };
  } catch (error) {
    console.error('[GanttChart] Failed to load CMO options:', error);
    return { status: false, data: [] };
  }
};

/**
 * Site / Plant options for the facility selector (FSD A), narrowed to the
 * selected CMO. Shape: [{ id, label }] where `id` is the SITE NAME — that is
 * what /fetchProjectDashboard filters on (its `cmoSite` payload field).
 *
 * Resolution copied from the dashboard's own siteOptions so the two filters
 * always offer the same list: the name is SiteName (cmo_sites.siteShortName)
 * falling back to mfgPlantShortName for a plant not yet linked to a site, and
 * the label pairs the two as "<Site> / <Plant>" — which is exactly the form the
 * FSD quotes ("HYD / Unit II", "Bidar / Unit IV").
 */
export const fetchGanttSiteOptionsAPI = async (cmoOrgId) => {
  try {
    const res = await fetchMfgPlantsAPI();
    const list = (res && res.data) || [];
    const selectedOrgId = cmoOrgId != null && cmoOrgId !== '' && cmoOrgId !== 'all'
      ? String(cmoOrgId)
      : null;
    const siteNameOf = (p) => {
      const site = p && p.SiteName != null ? String(p.SiteName).trim() : '';
      if (site) return site;
      return p && p.mfgPlantShortName != null ? String(p.mfgPlantShortName).trim() : '';
    };
    const seen = new Set();
    const options = [];
    list.forEach((p) => {
      const name = siteNameOf(p);
      if (!name) return;
      if (selectedOrgId != null && String(p.vendorMosymphonyID) !== selectedOrgId) return;
      if (seen.has(name)) return;
      seen.add(name);
      const plant = p && p.mfgPlantShortName != null ? String(p.mfgPlantShortName).trim() : '';
      options.push({ id: name, label: plant && plant !== name ? `${name} / ${plant}` : name });
    });
    return { status: true, data: options };
  } catch (error) {
    console.error('[GanttChart] Failed to load site options:', error);
    return { status: false, data: [] };
  }
};
